import React, { useState } from 'react';
import { Modal, Button, Form, ProgressBar } from 'react-bootstrap';
import { submitScorecardReview } from 'services/hvascorecard.service';

interface Question {
  id: number;
  title: string;
  scoreValue: number;
  progressValue: number;
  answerText: string;
  qaAgentComment: string | null;
  qaAgentName: string | null;
}

interface ScorecardReviewModalProps {
  show: boolean;
  onHide: () => void;
  questions: Question[];
  overallScore: string | number;
  callid: string;
  scorecardId: number;
  scorecardName: string;
  onUpdate?: () => void; // Prop para el callback de actualización
}

const ScorecardReviewModal: React.FC<ScorecardReviewModalProps> = ({
  show,
  onHide,
  questions,
  overallScore,
  callid,
  scorecardId,
  scorecardName,
  onUpdate
}) => {
  // eslint-disable-next-line
  const [reviews, setReviews] = useState<{ [key: number]: { score: number | null; comment: string | null } }>(
    questions.reduce(
      (acc, question) => ({
        ...acc,
        [question.id]: {
          score: question.scoreValue,
          comment: question.qaAgentComment
        }
      }),
      {}
    )
  );

  const handleScoreChange = (questionId: number, score: number) => {
    setReviews(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        score: prev[questionId]?.score === score ? null : score
      }
    }));
  };

  const handleCommentChange = (questionId: number, comment: string) => {
    setReviews(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], comment }
    }));
  };

  const handleClose = () => {
    onUpdate?.(); // Llamamos al callback de actualización si existe
    onHide();
  };

  const handleSubmit = async () => {
    try {
      const reviewData = {
        callId: callid,
        scorecardId: scorecardId,
        questions: questions.map(question => ({
          questionId: question.id,
          score: reviews[question.id]?.score ?? question.scoreValue,
          comment: reviews[question.id]?.comment || 'Reviewed'
        }))
      };

      await submitScorecardReview(reviewData);
      handleClose();
    } catch (error) {
      alert('Error submitting the review');
    }
  };

  return (
    <Modal show={show} centered onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {scorecardName} Review &nbsp;
          <span className="fs-9 fw-normal text-body-quaternary">
            (Call ID: {callid} - Scorecard: {scorecardId} )
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-6">
        <h5>Overall Score: {overallScore}</h5>
        <div
          className="p-3 pt-0 border rounded scrollbar"
          style={{ height: '550px', overflowY: 'auto' }}
        >
          {questions.map(question => (
            <div key={question.id} className="mb-4 fs-9 mt-3">
              <h6>{question.title}</h6>
              <ProgressBar
                className="lh-sm fw-bold"
                variant="primary-lighter"
                now={question.progressValue}
                label={`${question.scoreValue}`}
                style={{ height: '14px' }}
              />
              <p className="pt-2 mb-1">
                <strong>AI Comment:</strong> {question.answerText}
              </p>
              <div className="mb-2">
                <strong>Manual Score:</strong>
                <div className="btn-group d-flex" role="group">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                    <Button
                      key={score}
                      size="sm"
                      // eslint-disable-next-line
                      variant={reviews[question.id]?.score === score ? "primary" : "phoenix-secondary"}
                      onClick={() => handleScoreChange(question.id, score)}
                      className="flex-grow-1"
                    >
                      {score}
                    </Button>
                  ))}
                </div>
              </div>
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Enter comment here..."
                  value={reviews[question.id]?.comment || ''}
                  // eslint-disable-next-line
                  onChange={e => handleCommentChange(question.id, e.target.value)}
                />
              </Form.Group>
              {question.qaAgentName && (
                <p className="fs-9 text-primary mt-1">
                  Reviewed by: {question.qaAgentName}
                </p>
              )}
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Submit Review
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ScorecardReviewModal;
